import { Banknote, BookUser, CheckCircle2, CreditCard, Loader2, QrCode, Split, XCircle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney, parseMoney, type PayMethod, type Payment } from '@sense/shared';
import { useCart } from '../lib/cart';
import { chargeOnMachine, chargeRef, useTillTerminal, type Charge } from '../lib/cardpay';
import { usePricedCart } from '../lib/pricing';
import { useCustomer } from '../lib/customers';
import { LOYALTY_ENABLED, pointsFor } from '@sense/shared';
import { saveBill } from '../lib/ops';
import { printBill, whatsappReceipt } from '../lib/print';
import { directPrinter, openDrawer, usePrinter } from '../lib/printer';
import { reprintLast } from '../lib/reprint';
import { useSession } from '../lib/session';
import { haptic, toast } from '../lib/toast';
import { Button, Kbd, Sheet } from './ui';
import { isNativeApp } from '../lib/api';
import { useWide } from '../lib/useWide';
import { useT, tr } from '../lib/i18n';
import { onPayment, usePaymentAlerts } from '../lib/paymentAlerts';

// Each method has its own colour so the cashier sees at a glance what was picked.
const METHODS: Array<{ id: PayMethod; label: string; icon: typeof Banknote; key: string; on: string }> = [
  { id: 'cash', label: tr('Cash'), icon: Banknote, key: 'F5', on: 'bg-forest-700 text-white' },
  { id: 'upi', label: 'UPI', icon: QrCode, key: 'F6', on: 'bg-forest-700 text-white' },
  { id: 'card', label: tr('Card'), icon: CreditCard, key: 'F7', on: 'bg-forest-700 text-white' },
  { id: 'credit', label: tr('Credit'), icon: BookUser, key: 'F8', on: 'bg-forest-700 text-white' },
];

/** Quick-tender buttons: exact, then the next round notes above the total. */
function quickNotes(total: number): number[] {
  const out = new Set<number>([total]);
  for (const step of [5000, 10000, 50000, 200000]) {
    const v = Math.ceil(total / step) * step;
    if (v > total) out.add(v);
  }
  return [...out].sort((a, b) => a - b).slice(0, 4);
}

export function PaySheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const cart = useCart();
  const me = useSession((s) => s.me);
  const priced = usePricedCart();
  const customer = useCustomer(cart.customer?.id);
  const overLimit = (credit: number) =>
    customer?.credit_limit != null && customer.balance + credit > customer.credit_limit
      ? `${customer.name}’s khata limit is ${formatMoney(customer.credit_limit)} (owes ${formatMoney(customer.balance)} now).` : null;
  const totals = priced.totals;
  const due = totals.total;
  const tx = useT();
  // Payment options (Settings): the methods this store takes, and the one the sheet opens on.
  const allowed = me?.store.settings.payMethods?.length ? me.store.settings.payMethods : METHODS.map((m) => m.id);
  const methods = METHODS.filter((m) => allowed.includes(m.id));
  const startOn: PayMethod = me?.store.settings.defaultPay && allowed.includes(me.store.settings.defaultPay) ? me.store.settings.defaultPay : methods[0]?.id ?? 'cash';
  const [method, setMethod] = useState<PayMethod>(startOn);
  const [tendered, setTendered] = useState('');
  const [split, setSplit] = useState(false);
  const [parts, setParts] = useState<Record<PayMethod, string>>({ cash: '', upi: '', card: '', credit: '' });
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  // Card machine: the amount and mode go to the store's pin pad; the bill completes when it approves.
  const terminal = useTillTerminal();
  const [charge, setCharge] = useState<Charge | null>(null);
  const [machineRef, setMachineRef] = useState<string | null>(null); // approved card part (split bills)
  const stop = useRef({ cancelled: false });
  const waiting = charge?.status === 'pending';

  // Reset on close, so the next bill opens on Cash from its very first render (a fast Ctrl+Enter
  // never sees the previous bill's method).
  useEffect(() => {
    if (open) return;
    setMethod(startOn); setTendered(''); setSplit(false); setRef(''); setParts({ cash: '', upi: '', card: '', credit: '' });
    setCharge(null); setMachineRef(null);
  }, [open, startOn]);

  const upiId = me?.store.settings.upiId;
  const upiAmount = split ? parseMoney(parts.upi || '0') : due;
  useEffect(() => {
    if (!open || !upiId || !(method === 'upi' || (split && upiAmount > 0))) return setQr(null);
    const url = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(me?.store.settings.upiName || me?.store.name || '')}&am=${(upiAmount / 100).toFixed(2)}&cu=INR&tn=${encodeURIComponent('Bill ' + (me?.device.code ?? ''))}`;
    // QR library loads only when UPI is used.
    let live = true; // a newer amount must not be overwritten by an older QR
    void import('qrcode').then(({ default: QRCode }) => QRCode.toDataURL(url, { margin: 1, width: 360, color: { dark: '#1C1917', light: '#FFFFFF' } }))
      .then((d) => live && setQr(d), () => live && setQr(null));
    return () => { live = false; };
  }, [open, method, split, upiAmount, upiId, me]);

  const tenderPaise = tendered ? parseMoney(tendered) : due;
  const change = method === 'cash' && Number.isFinite(tenderPaise) ? Math.max(0, tenderPaise - due) : 0;
  const splitSum = useMemo(() => Object.values(parts).reduce((s, v) => s + (parseMoney(v || '0') || 0), 0), [parts]);

  const payments = (override?: string): Payment[] | string => {
    if (!split) {
      if (method === 'cash' && (!Number.isFinite(tenderPaise) || tenderPaise < due)) return tx('Cash received is less than the bill.');
      if (method === 'credit' && !cart.customer) return tx('Add the customer (F4) for a credit / khata bill.');
      if (method === 'credit' && overLimit(due)) return overLimit(due)!;
      const r = (override ?? ref).trim();
      return [r ? [method, due, r] : [method, due]];
    }
    if (splitSum !== due) return `Split amounts add up to ${formatMoney(splitSum)}, bill is ${formatMoney(due)}.`;
    if (parseMoney(parts.credit || '0') > 0 && !cart.customer) return 'Add the customer for the khata part.';
    if (overLimit(parseMoney(parts.credit || '0'))) return overLimit(parseMoney(parts.credit || '0'))!;
    return (Object.entries(parts) as Array<[PayMethod, string]>).map(([m, v]) => (m === 'card' && machineRef ? [m, parseMoney(v || '0'), machineRef] : [m, parseMoney(v || '0')]) as Payment).filter(([, a]) => a > 0);
  };

  const complete = async (print: boolean, override?: string) => {
    if (busy || !cart.lines.length) return;
    // An approval from the machine (override) completes even though this render still shows it waiting.
    if (waiting && override === undefined) return toast.info(tr('Waiting for the card machine. Cancel it first to finish another way.'));
    const p = payments(override);
    if (typeof p === 'string') return toast.error(p);
    setBusy(true);
    try {
      const bill = await saveBill(priced.items, p, { billDiscount: priced.billDiscount, customer: cart.customer, offers: priced.applied, approvals: cart.approvals ?? [] });
      cart.clear();
      haptic(40);
      onClose();
      toast.ok(`${tx('Saved {no}', { no: bill.bill_no })}${change ? ` · ${tx('change {a}', { a: formatMoney(change) })}` : ''}`, bill.customer && me
        ? { label: tr('WhatsApp'), run: () => void whatsappReceipt(bill, me) }
        : print ? undefined : { label: tr('Print'), run: () => void reprintLast() });
      const cash = p.some(([m]) => m === 'cash');
      if (print && me) await printBill(bill, me, { cash });
      else if (cash && directPrinter() && usePrinter.getState().config.drawer) void openDrawer().catch((e) => toast.error((e as Error).message));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** Send the amount (whole bill, or the card part of a split) and mode to the card machine. */
  const machineAmount = split ? parseMoney(parts.card || '0') : due;
  const sendToMachine = async () => {
    if (!terminal || waiting || busy) return;
    if (!Number.isFinite(machineAmount) || machineAmount < 100) return toast.error(split ? tr('Enter the card amount first.') : tr('The card machine takes ₹1 or more.'));
    const mode = !split && method === 'upi' ? 'upi' : 'card';
    stop.current = { cancelled: false };
    setCharge({ id: '', status: 'pending', amount: machineAmount, mode, message: 'Sending to the machine…' });
    try {
      const billRef = `${me?.device.code ?? 'T'}-${Date.now().toString(36).toUpperCase()}`;
      const c = await chargeOnMachine(terminal.id, machineAmount, mode, billRef, setCharge, stop.current);
      if (c.status !== 'approved') return;
      haptic(40);
      if (split) { setMachineRef(chargeRef(c)); toast.ok(tr('Card part {0} approved', { 0: formatMoney(c.amount) })); }
      else void completeRef.current(true, chargeRef(c));
    } catch (e) {
      setCharge({ id: '', status: 'failed', amount: machineAmount, mode, message: (e as Error).message });
    }
  };
  const sendRef = useRef(sendToMachine);
  sendRef.current = sendToMachine;

  // Window-level so F5–F8 never reach the browser (F5 would reload) wherever focus is.
  const cashRef = useRef<HTMLInputElement>(null);
  // Computers: Complete & print. Phones and the Android app: just Complete payment.
  const printFirst = useWide() && !isNativeApp();
  const printFirstRef = useRef(printFirst);
  printFirstRef.current = printFirst;
  const allowedRef = useRef(allowed);
  allowedRef.current = allowed;
  const completeRef = useRef(complete);
  completeRef.current = complete;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') { e.preventDefault(); void completeRef.current(printFirstRef.current && !(e.ctrlKey || e.metaKey)); return; }
      // Digits typed before the cash field has focus (fast cashiers press F10 then 700) still count.
      const t = e.target as HTMLElement;
      if (/^[0-9.]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey && cashRef.current && t !== cashRef.current && (!t.closest('[role=dialog]') || !['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) {
        e.preventDefault();
        setTendered((v) => v + e.key);
        cashRef.current.focus();
        return;
      }
      if (e.key === 'F9') { e.preventDefault(); void sendRef.current(); return; }
      const m = METHODS.find((x) => x.key === e.key && allowedRef.current.includes(x.id));
      if (m) { e.preventDefault(); setSplit(false); setMethod(m.id); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Payment alerts (Android): the phone hears the UPI app say the money came in, and the bill completes.
  const alerts = usePaymentAlerts((x) => x.granted);
  const openedAt = useRef(0);
  useEffect(() => { if (open) openedAt.current = Date.now(); }, [open]);
  useEffect(() => {
    if (!open || split || method !== 'upi') return;
    return onPayment((p) => {
      if (p.amount !== due || p.ts < openedAt.current - 10_000) return;
      toast.ok(tx('Received {a} · {from}', { a: formatMoney(p.amount), from: [p.app, p.payer].filter(Boolean).join(' · ') }));
      void completeRef.current(printFirstRef.current, [p.app, p.payer].filter(Boolean).join(' · '));
    });
  }, [open, split, method, due]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Sheet open={open} onClose={() => { if (waiting) stop.current.cancelled = true; onClose(); }} title={tx('Payment')} footer={
      printFirst ? (
        <div className="grid grid-cols-[auto_1fr] gap-2 pb-1">
          <Button variant="outline" size="lg" disabled={busy || waiting} onClick={() => void complete(false)} title={tr('Complete without printing (Ctrl Enter)')}>{tx('No print')}</Button>
          <Button size="lg" disabled={busy || waiting} onClick={() => void complete(true)}>{busy ? tx('Saving…') : <>{tx('Complete & print')} <span className="hidden lg:inline"><Kbd>{tr('Enter')}</Kbd></span></>}</Button>
        </div>
      ) : (
        // Phones and the Android app: one button; the receipt prints from the Saved notice or Transactions.
        <Button size="lg" className="w-full" disabled={busy || waiting} onClick={() => void complete(false)}>{busy ? tx('Saving…') : tx('Complete payment')}</Button>
      )}>
      <div className="flex flex-col gap-4 pb-2">
        <div className="flex items-end justify-between">
          <div>
            <div className="text-xs font-semibold text-muted">{tx('Amount due')} · {tx(cart.lines.length === 1 ? '1 item' : '{n} items', { n: cart.lines.length })}</div>
            <div className="num text-3xl font-bold">{formatMoney(due)}</div>
            {priced.applied.length > 0 && <div className="text-sm font-bold text-t-amber-fg">{tx('Customer saves {a} with offers', { a: formatMoney(priced.applied.reduce((a, o) => a + o.amount, 0)) })}</div>}
            {LOYALTY_ENABLED && cart.customer && pointsFor(due) > 0 && <div className="text-sm font-bold text-t-violet-fg">{tr('{0} earns', { 0: cart.customer.name })} {pointsFor(due)} {tr('points')}</div>}
          </div>
          <button onClick={() => setSplit(!split)} className={`flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-bold ${split ? 'bg-forest-700 text-white' : 'bg-forest-50 text-primary'}`}><Split size={16} />{tx('Split')}</button>
        </div>

        {!split && (
          <div role="radiogroup" aria-label="Payment method" className={`grid gap-2 ${['grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][methods.length - 1] ?? 'grid-cols-4'}`}>
            {methods.map((m) => (
              <button key={m.id} role="radio" aria-checked={method === m.id} onClick={() => setMethod(m.id)}
                className={`flex h-[72px] flex-col items-center justify-center gap-1 rounded-3xl text-sm font-bold ${method === m.id ? m.on : 'border border-line bg-white text-ink'}`}>
                <m.icon size={22} strokeWidth={1.75} />{tx(m.label)}<span className="hidden text-[10px] opacity-70 lg:block">{m.key}</span>
              </button>
            ))}
          </div>
        )}

        {!split && method === 'cash' && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-bold text-muted">{tx('Cash received (type digits)')}</span>
              <input ref={cashRef} inputMode="decimal" value={tendered} onChange={(e) => setTendered(e.target.value)} placeholder={(due / 100).toFixed(2)}
                className="num h-14 rounded-xl border border-line px-4 text-2xl outline-none focus:border-forest-600" />
            </label>
            <div className="grid grid-cols-4 gap-2">
              {quickNotes(due).map((v, i) => (
                <button key={v} onClick={() => setTendered((v / 100).toString())} className={`num h-12 rounded-full border text-base font-semibold text-primary ${tenderPaise === v ? 'border-forest-700 bg-t-green' : 'border-line bg-white'}`}>
                  {i === 0 ? tx('Exact') : formatMoney(v, { whole: true })}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-line bg-forest-50 px-4 py-3">
              <span className="text-sm font-bold text-primary">{tx('Return change')}</span>
              <span className="num text-3xl font-semibold text-primary">{formatMoney(change)}</span>
            </div>
          </div>
        )}

        {!split && method === 'upi' && (
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            {qr ? <img src={qr} alt={tr('UPI QR for {0}', { 0: formatMoney(due) })} className="h-44 w-44 shrink-0 rounded-xl border border-line" />
              : <div className="flex w-full items-center justify-center rounded-xl border-2 border-dashed border-line bg-surface-2 p-4 text-center text-xs font-semibold text-ink-2 sm:h-40 sm:w-40 sm:shrink-0">{tx('Add your UPI ID in Settings › Payment options to show a QR with the exact amount')}</div>}
            <div className="flex w-full min-w-0 flex-col gap-2 text-sm text-muted sm:flex-1">
              <b className="text-base text-ink">{tx('Ask the customer to scan')}</b>
              {alerts ? (
                <span role="status" className="flex items-center gap-2 rounded-xl bg-t-green px-3 py-2 font-semibold text-t-green-fg">
                  <Loader2 size={16} className="shrink-0 animate-spin" />{tx('Waiting for {a} on your UPI app · completes by itself', { a: formatMoney(due) })}
                </span>
              ) : tx('Confirm the payment on your UPI app, then complete the bill.')}
              <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder={tx('UTR / reference (optional)')} className="h-11 w-full min-w-0 rounded-xl border border-line px-3 text-ink outline-none focus:border-primary" />
            </div>
          </div>
        )}

        {!split && (method === 'credit' || (method === 'card' && !waiting)) && (
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-bold text-muted">{method === 'card' ? tx('Approval code / last 4 digits (optional)') : cart.customer ? tx('Add to {name}’s khata · owes {a} now', { name: cart.customer.name, a: formatMoney(customer?.balance ?? 0) }) : tx('Credit needs a customer — press F4')}</span>
            <input value={ref} onChange={(e) => setRef(e.target.value)} className="h-12 rounded-xl border border-line px-3 outline-none focus:border-forest-600" />
          </label>
        )}

        {terminal && ((!split && (method === 'card' || method === 'upi')) || (split && parseMoney(parts.card || '0') > 0)) && (
          <MachinePanel terminal={terminal.label} amount={machineAmount} mode={!split && method === 'upi' ? 'upi' : 'card'} charge={charge} approvedPart={split ? machineRef : null}
            onSend={() => void sendToMachine()} onCancel={() => { stop.current.cancelled = true; setCharge((c) => (c ? { ...c, message: 'Cancelling…' } : c)); }} />
        )}

        {split && (
          <div className="flex flex-col gap-2">
            {methods.map((m) => (
              <label key={m.id} className="flex items-center gap-3">
                <span className="flex w-24 shrink-0 items-center gap-2 text-sm font-bold"><m.icon size={18} />{tx(m.label)}</span>
                <input inputMode="decimal" value={parts[m.id]} onChange={(e) => setParts({ ...parts, [m.id]: e.target.value })}
                  onFocus={() => { if (!parts[m.id] && splitSum < due) setParts({ ...parts, [m.id]: ((due - splitSum) / 100).toString() }); }}
                  className="num h-12 min-h-12 w-full min-w-0 flex-1 rounded-xl border border-line px-3 text-lg outline-none focus:border-forest-600" placeholder="0" />
              </label>
            ))}
            <div className={`text-sm font-bold ${splitSum === due ? 'text-primary' : 'text-danger'}`}>
              {splitSum === due ? tx('Amounts match the bill.') : tx('Remaining {a}', { a: formatMoney(due - splitSum) })}
            </div>
            {qr && <img src={qr} alt={tr('UPI QR for the UPI part')} className="h-36 w-36 self-center rounded-xl border border-line" />}
          </div>
        )}
      </div>
    </Sheet>
  );
}

/** Card machine status inside the payment sheet. */
function MachinePanel({ terminal, amount, mode, charge, approvedPart, onSend, onCancel }: { terminal: string; amount: number; mode: 'card' | 'upi'; charge: Charge | null; approvedPart: string | null; onSend(): void; onCancel(): void }) {
  if (approvedPart) {
    return <div className="flex items-center gap-3 rounded-2xl bg-t-green px-4 py-3 text-sm font-bold text-t-green-fg" role="status"><CheckCircle2 size={22} />{tr('Card part approved · {0}', { 0: approvedPart })}</div>;
  }
  if (charge?.status === 'pending') {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-line bg-paper px-4 py-3" role="status" aria-live="polite">
        <Loader2 size={22} className="shrink-0 animate-spin text-primary" />
        <div className="min-w-0 flex-1">
          <div className="font-bold">{tr('{0} on {1}', { 0: formatMoney(charge.amount), 1: terminal })}</div>
          <div className="text-sm text-muted">{mode === 'upi' ? tr('Customer scans the QR on the machine') : tr('Customer taps, inserts or swipes the card')}</div>
          {charge.message && !/sent to the machine|waiting for the customer/i.test(charge.message) && <div className="text-xs font-bold text-muted">{charge.message}</div>}
        </div>
        <Button variant="outline" size="sm" onClick={onCancel}>{tr('Cancel')}</Button>
      </div>
    );
  }
  const bad = charge && charge.status !== 'approved';
  return (
    <div className="flex flex-col gap-2">
      {bad && (
        <div className="flex items-start gap-3 rounded-2xl bg-t-red px-4 py-3 text-sm font-bold text-danger" role="alert">
          <XCircle size={20} className="mt-0.5 shrink-0" />
          <span>{charge.status === 'cancelled' ? tr('Cancelled on the machine.') : charge.status === 'declined' ? tr('Declined{0}. Try again or take another payment.', { 0: charge.message ? `: ${charge.message}` : '' }) : charge.message ?? tr('The card machine did not answer.')}</span>
        </div>
      )}
      <Button size="lg" variant="secondary" onClick={onSend} className="w-full">
        <CreditCard size={20} />{bad ? tr('Send again') : tr('Send {0} to {1}', { 0: formatMoney(amount), 1: terminal })}{mode === 'upi' ? tr(' · UPI QR') : ''}<span className="hidden lg:inline"><Kbd>F9</Kbd></span>
      </Button>
    </div>
  );
}
